import { EventEmitter } from 'events';
import { Logger } from '../support/Logger';

type IUnsubscribe = () => void;

class EventBus {
    private events: EventEmitter;
    constructor(){
        this.events = new EventEmitter();
    }
    emit = <TPayload>(name: string, payload?: TPayload) => {
        this.events.emit(name, payload);
    };

    on = <TPayload>(name: string, listener: (payload?: TPayload) => void): IUnsubscribe => {
        this.events.addListener(name, listener);

        return () => {
            this.events.removeListener(name, listener);
        };
    };
}

/**
 * An event bus that BUFFERS every emitted event (last 100 per name, for the whole
 * session) and REPLAYS that buffer to each new subscriber on the next microtask.
 * This lets a late subscriber catch up on events it missed before it subscribed.
 *
 * CONTRACT: because of the replay, every `on()` handler MUST be idempotent. A handler
 * may be invoked with the same payload more than once — on the initial replay, and
 * again every time the consumer resubscribes (e.g. a component that resubscribes on
 * each state change replays the whole buffer each time). Handlers that accumulate
 * (e.g. `total += x`) or trigger one-shot side effects will misbehave: dedupe against
 * your own state, or compute from source rather than accumulating.
 *
 * IH-6926 was caused by a handler that violated this contract (the recording timer
 * added "catch-up" seconds on a replayed APP_FOREGROUNDED, double-counting elapsed
 * time). Existing consumers that get this right: GlobalRecordingModals (hasShown*
 * dedupe guards) and Stopwatch-based timers (compute elapsed, never accumulate).
 */
export class BufferedEventBus {
    static inject = () => [EventBus, Logger];
    constructor(private events: EventBus, private log: Logger){

    }

    private buffer: { [eventName: string]: any[]} = {};

    emit = <TPayload>(name: string, payload?: TPayload) => {
        try {
            this.events.emit(name, payload);
            this.addToBuffer(name, payload);
        } catch (err: any){
            // log but don't throw - emit is used to decouple concerns,
            // so expectation is that a failure in a handler won't crash the emitter
            this.log.error(`Failed to emit event "${name}": ${err.toString()}`);
        }
    };

    private addToBuffer = (name: string, payload?: any) => {
        if (!this.buffer[name]){
            this.buffer[name] = [];
        }

        this.buffer[name].push(payload);

        // limit buffer to last 100
        this.buffer[name] = this.buffer[name].slice(-100);
    };

    on = <TPayload>(name: string, listener: (payload?: TPayload) => void): IUnsubscribe => {
        // next tick, playback what was missed
        Promise.resolve().then(() => {
            (this.buffer[name] || []).forEach((payload: any | undefined) => {
                try {
                    listener(payload);
                }
                catch (err: any){
                    this.log.error(`failed catching listener up with buffer for ${name}. Error: ${err.toString()}`);
                }
            });
        });
        return this.events.on(name, listener);
    };
}
